'use client'

import { ArrowButton } from '@/components/ui/arrow-button'
import { ResourceCard } from '@/components/ResourceCard'
import { useCarouselScroll } from '@/hooks/useCarouselScroll'
import type { ResourceCardData } from '@/utilities/resourceCardData'

interface Props {
  resources: ResourceCardData[]
  /** Names the region, e.g. "Webinars carousel". */
  label: string
  /** Lower-case plural for the pager buttons - "Previous Newsroom" doesn't read as a phrase. */
  pluralNoun: string
}

export function ResourceCardCarousel({ resources, label, pluralNoun }: Props) {
  // autoplayDelay 0 turns auto-advance off: with no pause control it would fail WCAG 2.2.2.
  const { scrollRef, isAtStart, isAtEnd, scrollToIndex, handleKeyDown } = useCarouselScroll({
    itemCount: resources.length,
    autoplayDelay: 0,
  })

  // Derived from the count rather than measured, because measuring would render the pager
  // server-side then yank it after hydration. Two 320px cards plus the 32px gap need 672px,
  // which the container only reaches at md - hence `md:hidden`, not `sm:hidden`.
  const count = resources.length
  const pagerVisibility =
    count > 3 ? '' : count === 3 ? 'lg:hidden' : count === 2 ? 'md:hidden' : null

  const step = (direction: -1 | 1) => {
    const el = scrollRef.current
    if (!el) return
    const cards = el.querySelectorAll<HTMLElement>('[data-card]')
    if (cards.length < 2) return
    const pitch = cards[1].offsetLeft - cards[0].offsetLeft
    if (pitch <= 0) return
    const current = Math.round(el.scrollLeft / pitch)
    scrollToIndex(current + direction)
  }

  return (
    <div className="flex flex-col gap-6 lg:gap-8">
      {/* Scroller and focusable region are the same element, so keyboard users get one stop
          that scrolls. `relative` is required: useCarouselScroll reads child offsetLeft. */}
      <div
        ref={scrollRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={`${label} carousel`}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className="relative overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black"
      >
        <ul role="list" className="flex gap-8">
          {resources.map((resource) => (
            <li
              key={resource.key}
              data-card
              className="w-[300px] shrink-0 snap-start sm:w-[320px] lg:w-[calc((100%-64px)/3)]"
            >
              <ResourceCard resource={resource} />
            </li>
          ))}
        </ul>
      </div>

      {pagerVisibility !== null && (
        <div className={`flex items-center justify-end gap-2 ${pagerVisibility}`}>
          <ArrowButton
            direction="left"
            backgroundColor="lime"
            size="medium"
            variant="minimal"
            animated
            aria-label={`Previous ${pluralNoun}`}
            onClick={() => step(-1)}
            disabled={isAtStart}
          />
          <ArrowButton
            direction="right"
            backgroundColor="lime"
            size="medium"
            variant="minimal"
            animated
            aria-label={`Next ${pluralNoun}`}
            onClick={() => step(1)}
            disabled={isAtEnd}
          />
        </div>
      )}
    </div>
  )
}
